// src/pages/copilot/hooks/useCopilotSession.js
import { useEffect, useState, useCallback } from 'react';
import api from '../../../lib/axios';

const PATH = 'api/v1/insights';

// Synchronous request→response chat: send a message, wait for the HTTP
// response, append the assistant reply. `streaming` is simply "a request is
// in flight" — it drives the Thinking… loader. No SSE/sockets: the agent
// returns one final answer, so there's nothing to stream.
export function useCopilotSession(sessionId) {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);

  // Load existing session + history
  useEffect(() => {
    if (!sessionId) { setSession(null); setMessages([]); return; }
    let cancelled = false;
    api.get(`${PATH}/sessions/${sessionId}`)
      .then((r) => {
        if (cancelled) return;
        setSession(r.data.session);
        setMessages(r.data.messages || []);
      })
      .catch((err) => console.error('Insights session load failed:', err));
    return () => { cancelled = true; };
  }, [sessionId]);

  const send = useCallback(async (text) => {
    if (!sessionId || !text?.trim() || streaming) return;

    // Optimistically show the user's message, then flip the loader on.
    const tempId = `tmp-${Date.now()}`;
    setMessages((ms) => [...ms, {
      id: tempId, role: 'user', kind: 'text',
      content: { text }, created_at: new Date().toISOString(),
    }]);
    setStreaming(true);

    try {
      const r = await api.post(`${PATH}/sessions/${sessionId}/message`, { text });
      const { message, reply } = r.data;
      // Reconcile the optimistic user message with the persisted one, append reply.
      setMessages((ms) => [
        ...ms.map((m) => (m.id === tempId ? message : m)),
        reply,
      ]);
    } catch (err) {
      console.error('Send failed:', err);
      setMessages((ms) => [...ms, {
        id: `err-${Date.now()}`, role: 'assistant', kind: 'error',
        content: { text: 'Something went wrong sending that. Please try again.' },
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setStreaming(false);
    }
  }, [sessionId, streaming]);

  return { session, messages, streaming, send };
}
